import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Student, StudentInfo } from "../../../../../store/onboarding/types";
import React, { Component } from "react";
import { Button, Col, Modal, Spinner } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { toastr } from "react-redux-toastr";
import { faPlusCircle, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { allowAlphaNumbers, escapeSpecialCharacter } from "../../../../../utils/NamesUtils";

type PropsFromState = {
  isLoading: {
    getStudents: boolean;
    addStudents: boolean;
  };
  error?: {
    addStudents?: string | Array<any>;
  };
  modalShow: boolean;
  existingStudents: number[];
  isStudentRosterUploaded?: boolean;
  fromStudentInviteModal?: boolean;
  newStudents?: number[];
  isCoach?: boolean;
  isDistrictAdmin?: boolean;
  studentsRoster: StudentInfo[];
};

type DispatchProps = {
  addStudents: (
    students: Array<Student>,
    addStudentsToEvidence?: boolean,
    isStudentRosterUploaded?: boolean,
    fromStudentInviteModal?: boolean
  ) => any;
  onModalHide: () => void;
  getStudents: () => any;
  clearAddStudentsError: () =>  any;
  resetFromStudentInviteModal: () => any;
};

type Props = PropsFromState & DispatchProps;

type State = {
  students: Array<Student>;
  duplicateStudentIdsExist: boolean;
};

class StudentsInviteIndividuallyModal extends Component<Props, State> {
  emptyStudent: Student = {
    first_name: "",
    last_name: "",
    unique_id: "",
  };

  state: Readonly<State> = {
    students: [],
    duplicateStudentIdsExist: false
  };

  componentDidUpdate(
    prevProps: Readonly<Props>,
    prevState: Readonly<State>
  ): void {
    if (prevProps.isLoading.addStudents && !this.props.isLoading.addStudents) {
      let errorMsg = this.props.error!.addStudents;
      if (!errorMsg && this.props.fromStudentInviteModal) {
        const successcount = this.props.newStudents!.length;
        
        if (this.props.existingStudents && this.props.existingStudents.length > 0) {
          this.state.students.map((student,index) => {
            let input = document.getElementById(`unique_id_${index}`);
            let msg = document.getElementById(`unique_id_msg_${index}`);
            if(this.props.existingStudents.includes(Number(student.unique_id))) {
              if (input) {
                input.style.border = "1px solid red";
              }
              if (msg) {
                msg.innerHTML = "Student ID already exists. Please use a different Student ID.";
              }
            }
            if(this.props.newStudents!.length && this.props.newStudents!.includes(Number(student.unique_id))) {
              if (input) {
                input.style.removeProperty("border");
              }
              if (msg) {
                msg.innerHTML = "";
              }
            }
          })
        } else if (successcount && successcount > 0) {
          toastr.success(
            "Success",
            `Student(s) added successfully! ${successcount} new student(s) added.`
          );
          this.props.resetFromStudentInviteModal();
        }
        if(!this.props.existingStudents!.length) {
          this.props.onModalHide();
        }
      }
      if (errorMsg && !Array.isArray(errorMsg) && !this.props.isStudentRosterUploaded && this.props.fromStudentInviteModal) {
        toastr.error("Failed", errorMsg!);
        // Clear the error from the Redux store
        this.props.clearAddStudentsError?.();
        this.props.resetFromStudentInviteModal();
      }
    }
  }

  handleOnEnter = () => {
    this.setState({
      students: [this.emptyStudent],
    });
  };

  handleAddStudent = () => {
    this.setState((prevState) => ({
      ...prevState,
      students: [...prevState.students, this.emptyStudent],
    }));
  };

  handleStudentChange = (selectedIndex: number) => (
    event: React.FormEvent<any>
  ) => {
    const { name, value } = event.target as HTMLInputElement;
    let sanitizedValue = value;

  // Restrict first/last name fields
    if (name === "first_name" || name === "last_name") {
      sanitizedValue = escapeSpecialCharacter(value);
    }

  // Restrict student ID field
    if (name === "unique_id") {
      sanitizedValue = allowAlphaNumbers(value);
    }
    this.setState((state) => ({
      ...state,
      students: state.students.map((student: Student, index) => {
        if (index === selectedIndex) {
          return {
            ...student,
            [name]: sanitizedValue,
          };
        }
        return student;
      }),
    }), () =>  {
      if (name === "unique_id") {
        sanitizedValue = value;
        const input = document.getElementById(`unique_id_${selectedIndex}`);
        const msg = document.getElementById(`unique_id_msg_${selectedIndex}`);
        if (input) input.style.border = "";
        if (msg) msg.innerHTML = "";

        const studentIds = this.state.students.map((student: Student) => student.unique_id);
        const duplicateStudentIds: any = {};
        const frequency: any = {};
      
        // Build frequency map (exclude empty emails)
        studentIds.forEach(id => {
          if (id && id.trim()) {
            frequency[id] = (frequency[id] || 0) + 1;
          }
        });

        // Identify duplicates + highlight UI
        studentIds.forEach((id, index) => {
          if (id && id.trim() && frequency[id] > 1) {
            duplicateStudentIds[index] = id;

            const input = document.getElementById(`unique_id_${index}`);
            const msg = document.getElementById(`unique_id_msg_${index}`);

            if (input) input.style.border = "1px solid red";
            if (msg) msg.innerHTML = "The highlighted Student ID is the same as another. Please use a different Student ID.";
          }
        });
        
        // If duplicates exist, adjust icons and STOP execution
        if (Object.keys(duplicateStudentIds).length > 0) {
          this.setState({
            ...this.state,
            duplicateStudentIdsExist: true
          })
        } else {
          if(this.state.duplicateStudentIdsExist){
            studentIds.forEach((_, selectedIndex) => {
              const input = document.getElementById(`unique_id_${selectedIndex}`);
              const msg = document.getElementById(`unique_id_msg_${selectedIndex}`);
              if (input) input.style.border = "";
              if (msg) msg.innerHTML = "";
            })
          }
          this.setState({
            ...this.state,
            duplicateStudentIdsExist: false
          })
        }
      }
    });
  };

  handleStudentsAdd = (event: React.FormEvent<any>) => {
    event.preventDefault();
    this.props.addStudents(this.state.students, false, false, true);
  };

  handleModalClose = () => {
    const { getStudents, onModalHide, isLoading, studentsRoster } = this.props;
    if (!isLoading.addStudents) {
      if(!(studentsRoster.length || isLoading.getStudents)) {
        getStudents();
      }
      onModalHide();
    }
  };

  handleRemove = (recIndex:number) => {
    const students = this.state.students;
    this.setState((prevState) => ({
      ...prevState,
      students: [...students.filter((student, index) => recIndex !== index)],
    }));
  };

  render() {
    const { students } = this.state;
    return (
      //@ts-ignore
      <Modal
        animation={false}
        backdropClassName="customDarkModalBackdrop in"
        show={this.props.modalShow}
        //@ts-ignore
        onShow={this.handleOnEnter}
        onHide={this.handleModalClose}
        size="lg"
      >
        <Modal.Header
          closeButton
          className="purpleModalHeader orangeModalHeader"
        >
          <Modal.Title>Add Individually</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <h4 className="mb-3"> {
            this.props.isCoach 
            ? (
              <>
                Enter the student's details, then click <strong>Add</strong>. Student(s) will be added to your district
                but not assigned to any teachers. To have them assigned, please contact your district
                administrator.
              </>
            )
            : this.props.isDistrictAdmin
              ? (
                <>
                  Enter the student's details, then click <strong>Add</strong>. Student(s) will be added to your
                  district but not assigned to any teachers. You can assign them to teachers by viewing
                  the teacher’s details in the My Staff tab.
                </>
              )
              : ``
          }
            
          </h4>
          <div className="mb-1" style={{color: "red"}}>
            <small>
              <i>Fields marked with <strong>*</strong> are required.</i>
            </small>
          </div>
            <Form id="newInviteForm" onSubmit={this.handleStudentsAdd}>
              {students.map((student, index) => (
              <div className="d-flex" key={index.toString()}>
                <div className="studentInviteContainer">
                      <Form.Row key={index.toString()}>
                        <Col>
                          <Form.Control
                            required
                            placeholder="First Name *"
                            name="first_name"
                            onChange={this.handleStudentChange(index)}
                            value={student.first_name}
                          />
                        </Col>
                        <Col>
                          <Form.Control
                            required
                            placeholder="Last Name *"
                            name="last_name"
                            onChange={this.handleStudentChange(index)}
                            value={student.last_name}
                          />
                        </Col>
                        <Col>
                          <Form.Control
                            required
                            placeholder="Student ID *"
                            name="unique_id"
                            id={`unique_id_${index}`}
                            onChange={this.handleStudentChange(index)}
                            value={student.unique_id}
                          />
                          <div id={`unique_id_msg_${index}`} style={{color:"red"}}></div>
                        </Col>
                      </Form.Row>
                </div>
                <div className="d-flex align-items-center w-20">
                  {(index > 0) ? 
                    <span className="mx-1 removeIconBtn" onClick={() => this.handleRemove(index)}>
                      <FontAwesomeIcon
                        icon={faTimesCircle}
                        color="red"
                        size="lg"
                        title="Remove"
                        /></span>

                  :  <span style={{marginRight: "30px"}}></span>}
                </div>
              </div>
              ))}
            </Form>

          <div className="leftActions d-flex flex-column align-items-start mt-3">
            <Button
              bsPrefix="b"
              className="blueBtnSm"
              onClick={this.handleAddStudent}
            >
              <FontAwesomeIcon icon={faPlusCircle} className="mr-1" style={{ color: "white", marginRight: "6px" }} />
              Add Another
            </Button>
          </div>

          <hr/>
          <div className="d-flex justify-content-end pr-0">
            <div className="d-flex" style={{ gap: "8px" }}>
              <Button
                bsPrefix="b"
                disabled={this.props.isLoading.addStudents}
                onClick={this.props.onModalHide}
                className="whiteBtnSm"
              >
                Cancel
              </Button>
              <button form="newInviteForm" type="submit" className="blueBtnSm" 
              disabled={ students.some((student) => !student?.first_name?.trim() ||
                !student?.last_name?.trim() ||
                !student?.unique_id?.trim())
                || this.state.duplicateStudentIdsExist}>
                Add{" "}
                {this.props.isLoading.addStudents && (
                  <Spinner animation="border" size="sm" />
                )}
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    );
  }
}

export default StudentsInviteIndividuallyModal;
